import * as signalR from '@microsoft/signalr';
import * as React from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import { Provider } from 'react-redux';
import { compose, createStore, Reducer } from 'redux';
import { install } from 'redux-loop';
import { clockTick, init, messageFromClientDataHub, RootAction, subToClientDataHubFailed } from './actions';
import App from './containers/App';
import { initialState, rootReducer } from './reducers/reducer';
import { HubChannel, Model } from './types/types';
import { only } from './utils';

// @ts-ignore
const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose
const enhancer = composeEnhancers(install())

const store = createStore<Model, RootAction, {}, null>(
  // Why is this type assertion necessary?
  // Where does the `Model | undefined` come from?
  rootReducer as Reducer<Model, RootAction>,
  initialState,
  enhancer,
)

store.dispatch(init())

const root = createRoot(document.getElementById('root') as HTMLElement); 

root.render(
  <Provider store={store}>
    <App />
  </Provider>
)

setInterval(function clockTicker(): void {
  store.dispatch(clockTick(new Date()))
}, 500)

// tslint:disable-next-line:no-let
let connectionsCounter = 0

export async function subToClientDataHub(): Promise<
  signalR.HubConnection | 'SKIPPING'
> {
  if (connectionsCounter > 0) {
    console.log('signalR connections already in progress; ignoring')
    return 'SKIPPING'
  }
  connectionsCounter++

  try {
    const connection = new signalR.HubConnectionBuilder()
      .withUrl('/hubs/client-data')
      .withAutomaticReconnect()
      .withServerTimeout(30000)
      .configureLogging(signalR.LogLevel.Information)
      .build()

    const listenedChannels: ReadonlyArray<HubChannel> = [
      'BroadcastMetricSamples',
      'BroadcastAlert',
      'BroadcastAlertsHistory',
      'BroadcastRjeConnectionStatus',
    ]

    listenedChannels.forEach(channel => {
      connection.on(only<HubChannel>(channel), data => {
        store.dispatch(messageFromClientDataHub(channel, data))
      })
    })

    // `.start()` should happen after all `.on()` listeners are registered, so
    // no messages are dropped.
    await connection.start()
    console.info('Started the SignalR connection')

    connection.onclose((error?: Error) => {
      const ourError = error || new Error('no reason given')
      console.warn('SignalR connection closed', { ourError })
      connectionsCounter--
      store.dispatch(subToClientDataHubFailed(ourError))
    })
    return connection
  } catch (error) {
    connectionsCounter--
    const msg = 'Failed to start the SignalR connection'
    console.error(msg, { error })
    throw error
  }
}

navigator.serviceWorker.getRegistrations().then(registrations => {
  for (const reg of registrations) {
    reg.unregister();
  }
});
